import React, {Component} from 'react';
import {connect} from 'react-redux';
import Editor from 'react-medium-editor';
import Cookies from 'js-cookie';
import {FormattedMessage, injectIntl} from 'react-intl';
import Select2 from 'react-select2-wrapper';

import ThreadFileUpload from './ThreadFileUpload';
import {threadFileUpload, threadInput, threadWorkflowStatusUpdate} from '../../redux/actions';
import {getCurrentThread} from '../../redux/selectors';
import {WhenGranted, withPermissions} from '../util/Permissions';
import ThreadAssignReplyStatus from './ThreadAssignReplyStatus';

import '../ThreadEvent/Message.module.css';
import styles from './ThreadInput.module.css';
import 'medium-editor/dist/css/medium-editor.css';
import {getSelect2Locale} from '../../util';
import cn from 'classnames';

class ThreadInput extends Component {

    constructor(props) {
        super(props);

        const assignReplyStatus = Cookies.get('assignReplyStatus');

        this.state = {
            content: '',
            templateSelectOpen: false,
            templateLoading: false,
            assignReplyStatus: (assignReplyStatus === undefined) ? true : (assignReplyStatus === 'true'),
        };

        this.onKeyUp = this.onKeyUp.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.toggleAssignReplyStatus = this.toggleAssignReplyStatus.bind(this);
        this.toggleTemplateSelect = this.toggleTemplateSelect.bind(this);
        this.onMessageTemplateSelect = this.onMessageTemplateSelect.bind(this);
    }

    onKeyUp(e) {
        if ((e.ctrlKey || e.metaKey) && (e.key === 'Enter' || e.keyCode === 13)) {
            e.preventDefault();
            e.stopPropagation();

            this.onSubmit();
        }
    }

    handleEditorInput = (e) => {
        const el = e.currentTarget;
        const hasBase64Image = !!el.querySelector('img[src^="data:image/"]');

        if (this.containsBase64Image(el)) {
            alert(this.props.intl.formatMessage({ id: 'messages.image_insert_not_allowed' }));
            this.removeBase64Images(el);
        }
    };

    handleRawDrop = (e) => {
        const hasImage = [...e.dataTransfer.items].some(item =>
            item.type.startsWith("image/")
        );

        if (hasImage) {
            e.preventDefault();
            e.stopPropagation();
            alert(this.props.intl.formatMessage({ id: 'messages.image_insert_not_allowed' }));
        }
    };


    attachDropListener() {
        if (!this.mediumElement ||
            !this.mediumElement.medium ||
            !this.mediumElement.medium.origElements) {
            return;
        }

        const el = this.mediumElement.medium.origElements;

        if (!el._dropHandlerAttached) {
            el.addEventListener('drop', this.handleRawDrop, true);
            el._dropHandlerAttached = true;
        }
    }


    containsBase64Image = (element) => {
        return !!element.querySelector('img[src^="data:image/"]');
    };

    removeBase64Images = (element) => {
        element.querySelectorAll('img[src^="data:image/"]').forEach((img) => img.remove());
    };

    async onSubmit() {
        if (this.props.isLoading || this.props.fileUploadInProgress) {
            return;
        }

        const content = this.getContent();
        this.setState((state) => Object.assign(state, {
            content
        }));

        const contentElement = document.createElement('DIV');
        contentElement.innerHTML = content;

        const textOnly = contentElement.textContent.trim();

        if (!textOnly && this.props.uploadedFiles.isEmpty()) {
            return;
        }

        let assignReplyStatus = null;

        if (this.props.isGranted('thread.edit.workflow') && this.state.assignReplyStatus) {
            assignReplyStatus = this.props.replyStatus;
        }

        try {
            await this.props.sendMessage(
                {message: content, _csrf_token: window.CSRF_ADD_MESSAGE},
                textOnly,
                assignReplyStatus
            );
        } catch (e) {
            return;
        }

        this.setContent('');
    }

    toggleAssignReplyStatus() {
        this.setState((state) => Object.assign(state, {
            assignReplyStatus: !state.assignReplyStatus,
        }))
    }

    toggleTemplateSelect() {
        this.setState((state) => Object.assign(state, {
            templateSelectOpen: !state.templateSelectOpen
        }));
    }

    componentDidMount() {
        if (this.props.hasAiReply) {
            this.setContent(
                [this.props.aiReply.reply, this.props.aiReply.signature]
                    .filter(Boolean)
                    .join('\n')
           );
        }

        setTimeout(() => {
            if (this.mediumElement && this.mediumElement.medium && this.mediumElement.medium.origElements) {
                this.mediumElement.medium.origElements.focus();
            }
        }, 0);

        this.attachDropListener();
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.templateSelectOpen && !prevState.templateSelectOpen) {
            this.messageTemplateSelect.el.select2('open');
        }

        if (this.state.assignReplyStatus !== prevState.assignReplyStatus) {
            Cookies.set('assignReplyStatus', this.state.assignReplyStatus);
        }

        if (!this.props.isLoading && prevProps.isLoading && this.mediumElement) {
            this.mediumElement.medium.origElements.focus();
        }

        this.attachDropListener();
    }

    async onMessageTemplateSelect(e) {
        if (!e.params.data.id) {
            return;
        }

        const messageTemplateId = parseInt(e.params.data.id);

        if (this.getContent() && !window.confirm(this.props.intl.formatMessage({id: 'message_template_confirm'}))) {
            return;
        }

        this.setState((state) => Object.assign(state, {
            templateLoading: true,
        }));

        const url = Routing.generate('imatic_communicator_messagetemplate_get', {
            messageTemplateId,
            threadId: this.props.thread.id
        });

        const response = await fetch(url, {credentials: 'include'});
        const data = await response.json();

        this.setState((state) => Object.assign(state, {
            templateSelectOpen: false,
            templateLoading: false,
        }));

        this.setContent(data.body);
    }

    setContent(content) {
        if (!this.mediumElement || !this.mediumElement.medium) {
            return;
        }

        this.mediumElement.medium.setContent(content);
    }

    getContent() {
        if (!this.mediumElement || !this.mediumElement.medium) {
            return '';
        }

        return this.mediumElement.medium.origElements.innerHTML;
    }

    render() {
        const isClosed = (
            this.props.workflowStatus.closed ||
            (this.props.workflowStatus.openForEditor && !this.props.isGranted('thread.edit.workflow'))
        );

        if (isClosed || !this.props.isGranted('thread.reply')) {
            return (
                <div className={styles.cannotReply}>
                    <FormattedMessage id="thread.cannot_reply"/>
                </div>
            );
        }

        return (
            <div className={styles.threadInput}>
                <div className={cn(styles.editorContainer, {
                    [styles.hasAiReply]: this.props.hasAiReply
                })}>
                    {this.state.templateSelectOpen
                        ? <Select2
                            ref={(el) => this.messageTemplateSelect = el}
                            data={this.props.messageTemplates.map((mt) => ({
                                id: mt.id,
                                text: mt.name,
                            }))}
                            options={{
                                width: '100%',
                                theme: 'bootstrap4',
                                language: getSelect2Locale(),
                            }}
                            disabled={this.state.templateLoading}
                            onSelect={this.onMessageTemplateSelect}
                        />
                        : (
                            this.props.isLoading
                                ? <div className={styles.disabledEditor} dangerouslySetInnerHTML={{__html: this.state.content}}/>
                                : <Editor
                                    onKeyUp={this.onKeyUp}
                                    ref={(el) => this.mediumElement = el}
                                    options={{
                                        placeholder: {
                                            text: this.props.intl.formatMessage({id: 'thread.message_placeholder'}),
                                        },
                                        toolbar: {
                                            buttons: ['bold', 'italic', 'underline', 'orderedlist', 'unorderedlist', 'anchor', 'h2', 'h3', 'quote'],
                                        },
                                    }}
                                    onInput={this.handleEditorInput}
                                />
                        )
                    }
                </div>
                <div className={styles.buttons}>
                    <WhenGranted permission="message_template.use">
                        <button type="button"
                                onClick={this.toggleTemplateSelect}
                                className={styles.toggleTemplateSelect}
                                title={this.props.intl.formatMessage({id: 'thread.action.use_template'})}
                        >
                            <svg width="22px" height="22px" viewBox="0 0 22 22" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                    <g transform="translate(-1.000000, -1.000000)" fill="#FFFFFF">
                                        <g transform="translate(1.000000, 1.000000)">
                                            <path d="M2,4 L0,4 L0,20 C0,21.1 0.9,22 2,22 L18,22 L18,20 L2,20 L2,4 L2,4 Z M20,0 L6,0 C4.9,0 4,0.9 4,2 L4,16 C4,17.1 4.9,18 6,18 L20,18 C21.1,18 22,17.1 22,16 L22,2 C22,0.9 21.1,0 20,0 L20,0 Z M20,16 L6,16 L6,2 L20,2 L20,16 L20,16 Z"/>
                                        </g>
                                    </g>
                                </g>
                            </svg>
                        </button>
                    </WhenGranted>
                    <WhenGranted permission="thread.edit.workflow">
                        <ThreadAssignReplyStatus checked={this.state.assignReplyStatus} onChange={this.toggleAssignReplyStatus}/>
                    </WhenGranted>
                    <ThreadFileUpload thread={this.props.thread} uploadedFiles={this.props.uploadedFiles}/>
                    <button type="button"
                            onClick={this.onSubmit}
                            className={styles.submit}
                            title={this.props.intl.formatMessage({id: 'thread.submit'})}
                    >
                        <svg width="21px" height="18px" viewBox="0 0 21 18" version="1.1"
                             xmlns="http://www.w3.org/2000/svg">
                            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                <g transform="translate(-2.000000, -3.000000)" fill="#FFFFFF">
                                    <polygon points="2.01 21 23 12 2.01 3 2 10 17 12 2 14"/>
                                </g>
                            </g>
                        </svg>
                    </button>
                </div>
            </div>
        );
    }
}

export default injectIntl(connect(
    (state) => {
        const thread = getCurrentThread(state);
        const threadType = state.getIn(['threadTypes', thread.threadType]);

        const aiReply = state.get('aiReplies').valueSeq().first();
        const promptMessageId = aiReply ? aiReply.get('promptMessage') : null;
        const lastMessage = state.get('threadEvents')
            ? state.get('threadEvents')
                .findLast((event) => event.get('type') === 'message' && !event.get('isAutomaticReply'))
            : null;
        const lastMessageId = lastMessage ? lastMessage.get('id') : null;

        const hasAiReply = aiReply != null
            && aiReply.reply != null
            && (promptMessageId === lastMessageId);

        return {
            thread,
            isLoading: state.getIn(['threadInputMeta', 'loading']) || state.getIn(['threadWorkflowStatusMeta', 'loading']),
            workflowStatus: state.getIn(['workflowStatuses', thread.workflowStatus]),
            replyStatus: state.getIn(['workflow', threadType.workflow]).replyStatus,
            messageTemplates: state.get('messageTemplates').valueSeq().sortBy((value) => value.name).toArray(),
            uploadedFiles: state.get('threadUploadedFiles'),
            fileUploadInProgress: state.getIn(['threadFileUploadMeta', 'inProgress']),
            hasAiReply,
            aiReply,
        };
    },
    (dispatch, {intl}) => ({
        sendMessage: async (body, textOnly, workflowStatus = null) => {
            try {
                await dispatch(threadFileUpload());
                if(textOnly) {
                    await dispatch(threadInput(body));
                    if (workflowStatus) {
                        await dispatch(threadWorkflowStatusUpdate(workflowStatus));
                    }
                }
            } catch (e) {
                if (window.confirm(intl.formatMessage({id: 'thread.confirm_reload'}))) {
                    location.reload(true);
                }

                throw e;
            }
        }
    }),
)(withPermissions(ThreadInput)));
